import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { motion } from "framer-motion"
import { useHistory } from "react-router-dom"
import { PhotoProvider, PhotoView } from "react-photo-view"
import {
    HiOutlineArrowLeft,
    HiOutlineBookmark,
    HiOutlineBuildingOffice2,
    HiOutlineCalendarDays,
    HiOutlineClock,
    HiOutlineHeart,
    HiOutlineHomeModern,
    HiOutlineIdentification,
    HiOutlineLockClosed,
    HiOutlineMapPin,
    HiOutlineMagnifyingGlass,
    HiOutlinePhone,
    HiOutlineSparkles,
    HiOutlineUserGroup,
} from "react-icons/hi2"
import { FaGraduationCap } from "react-icons/fa6"
import API from "../../Common/API"
import dummy from "../../Assets/Images/dummy.jpg.webp"
import {
    getInterestNotifications,
    getSentInterestIds,
    getShortlistedIds,
    hasInterestBeenSent,
    rankMatchProfiles,
    sendInterestNotification,
    toggleShortlistedProfile,
} from "../../Common/memberMatches"
import "react-photo-view/dist/react-photo-view.css"

const getFullName = (user) =>
    [user?.firstName, user?.lastName, user?.surname]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim() || "Member"

const formatDate = (value) => {
    if (!value) return "-"
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return "-"

    return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    })
}

const calculateAge = (birthdate) => {
    if (!birthdate) return "-"
    const today = new Date()
    const birthDate = new Date(birthdate)
    if (Number.isNaN(birthDate.getTime())) return "-"

    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1
    }

    return age
}

const renderList = (value) => {
    if (Array.isArray(value)) {
        return value
            .map((item) => (typeof item === "string" ? item : item?.text || item?.label || item?.value))
            .filter(Boolean)
            .join(", ") || "-"
    }

    return value || "-"
}

const getGalleryImages = (user) => {
    const images = user?.images?.length ? user.images : user?.previewUrls || []
    return images.filter((item) => item?.imageUrl)
}

const getMemberLocation = (user) =>
    [
        user?.occupationLocation,
        user?.contactInformation?.presentAddress?.city,
        user?.contactInformation?.presentAddress?.state,
    ]
        .filter(Boolean)
        .join(", ")

const buildSearchString = (profile) =>
    [
        profile?.firstName,
        profile?.lastName,
        profile?.surname,
        profile?.refId,
        profile?.occupation,
        profile?.occupationDetail,
        profile?.occupationLocation,
        profile?.contactInformation?.presentAddress?.city,
        profile?.contactInformation?.presentAddress?.state,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()

const getStoredPortalUser = () => {
    if (typeof window === "undefined") return {}

    try {
        return JSON.parse(window.localStorage.getItem("userDetails") || "{}")
    } catch (error) {
        return {}
    }
}

const MemberView = ({ match, portalMode = false }) => {
    const history = useHistory()
    const occupationOpts = useSelector((state) => state.auth.occupation || [])

    const resolveOccupation = (val) => {
        if (!val) return '-'
        const arr = Array.isArray(val) ? val : [val]
        const resolved = arr.map(item => {
            if (typeof item === 'string') {
                const found = occupationOpts.find(o => o.value === item || o.value?.toString() === item)
                return found ? found.text : item
            }
            return item?.text || item?.label || item?.value || ''
        }).filter(Boolean).join(', ')
        return resolved || '-'
    }
    const storedPortalUser = React.useMemo(() => getStoredPortalUser(), [])
    const routeUserId = match?.params?.id || ""
    const portalUserId = storedPortalUser?._id || storedPortalUser?.id || storedPortalUser?.userId || ""
    const initialMemberId = portalMode ? portalUserId : routeUserId
    const [approvedUsers, setApprovedUsers] = useState([])
    const [currentMemberId, setCurrentMemberId] = useState(initialMemberId)
    const [currentMember, setCurrentMember] = useState(null)
    const [planDetails, setPlanDetails] = useState(null)
    const [selectedMatchId, setSelectedMatchId] = useState("")
    const [shortlistedIds, setShortlistedIds] = useState([])
    const [sentInterestIds, setSentInterestIds] = useState([])
    const [incomingInterestNotifications, setIncomingInterestNotifications] = useState([])
    const [matchSearch, setMatchSearch] = useState("")
    const [loading, setLoading] = useState(true)
    const [feedback, setFeedback] = useState("")
    const [memberNotice, setMemberNotice] = useState("")
    const feedbackTimerRef = useRef(null)

    useEffect(() => {
        setCurrentMemberId(portalMode ? portalUserId : routeUserId)
    }, [portalMode, portalUserId, routeUserId])

    useEffect(() => {
        if (portalMode) {
            setApprovedUsers([])
            setCurrentMember(storedPortalUser || null)
            setPlanDetails(null)
            setLoading(false)
            setMemberNotice("Member login is working now. Matching profiles will show here after member-side matching APIs are connected.")
            return undefined
        }

        let active = true

        API.get("/user/getUserListByStatus/1").then((response) => {
            if (!active || response.status !== 200) return

            const users = response?.data?.data?.users || []
            setApprovedUsers(users)

            if (!portalMode && !routeUserId && users[0]?._id) {
                setCurrentMemberId(users[0]._id)
            }
        }).catch(() => {
            if (active) {
                setApprovedUsers([])
            }
        })

        return () => {
            active = false
        }
    }, [portalMode, routeUserId, storedPortalUser])

    useEffect(() => {
        if (portalMode) {
            return undefined
        }

        if (!currentMemberId) {
            setCurrentMember(null)
            setPlanDetails(null)
            setLoading(false)
            return
        }

        let active = true
        setLoading(true)

        Promise.allSettled([
            API.get(`/user/getUserDetailsById/${currentMemberId}`),
            API.get(`/plans/getSubscribedPlanDetailsByUserId/${currentMemberId}`),
        ]).then(([userRes, planRes]) => {
            if (!active) return

            setCurrentMember(userRes.status === "fulfilled" ? userRes.value?.data?.data || null : null)
            setPlanDetails(planRes.status === "fulfilled" ? planRes.value?.data?.data || null : null)
            setLoading(false)
        }).catch(() => {
            if (!active) return
            setCurrentMember(null)
            setPlanDetails(null)
            setLoading(false)
        })

        return () => {
            active = false
        }
    }, [currentMemberId, portalMode])

    useEffect(() => {
        setShortlistedIds(getShortlistedIds(currentMemberId))
        setSentInterestIds(getSentInterestIds(currentMemberId))
        setIncomingInterestNotifications(getInterestNotifications(currentMemberId))
    }, [currentMemberId])

    const rankedMatches = useMemo(() => rankMatchProfiles(currentMember, approvedUsers), [approvedUsers, currentMember])

    const filteredMatches = useMemo(() => {
        const keywords = matchSearch
            .toLowerCase()
            .trim()
            .split(" ")
            .filter(Boolean)

        if (keywords.length === 0) return rankedMatches

        return rankedMatches.filter((profile) => {
            const searchString = buildSearchString(profile)
            return keywords.every((keyword) => searchString.includes(keyword))
        })
    }, [matchSearch, rankedMatches])

    useEffect(() => {
        if (!filteredMatches.length) {
            setSelectedMatchId("")
            return
        }

        setSelectedMatchId((currentValue) => {
            if (currentValue && filteredMatches.some((profile) => profile._id === currentValue)) {
                return currentValue
            }

            return filteredMatches[0]._id
        })
    }, [filteredMatches])

    const selectedMatch = useMemo(
        () => filteredMatches.find((profile) => profile._id === selectedMatchId) || filteredMatches[0] || null,
        [filteredMatches, selectedMatchId]
    )

    const selectedMatchGallery = useMemo(() => getGalleryImages(selectedMatch), [selectedMatch])
    const currentMemberName = getFullName(currentMember)
    const currentMemberLocation = getMemberLocation(currentMember)
    const selectedMatchName = getFullName(selectedMatch)
    const selectedMatchLocation = getMemberLocation(selectedMatch)
    const selectedMainImage = selectedMatchGallery[0]?.imageUrl || dummy
    const shortlistedMatches = rankedMatches.filter((profile) => shortlistedIds.includes(profile._id))
    const sentInterestCount = sentInterestIds.length

    const updateFeedback = (message) => {
        setFeedback(message)
        window.clearTimeout(feedbackTimerRef.current)
        feedbackTimerRef.current = window.setTimeout(() => setFeedback(""), 2600)
    }

    useEffect(() => () => window.clearTimeout(feedbackTimerRef.current), [])

    const handleShortlistToggle = (profileId) => {
        const nextShortlist = toggleShortlistedProfile(currentMemberId, profileId)
        setShortlistedIds(nextShortlist)

        const profile = rankedMatches.find((item) => item._id === profileId)
        updateFeedback(
            nextShortlist.includes(profileId)
                ? `${getFullName(profile)} saved in your shortlist.`
                : `${getFullName(profile)} removed from your shortlist.`
        )
    }

    const handleSendInterest = (profile) => {
        if (!currentMember || !profile || hasInterestBeenSent(currentMemberId, profile._id)) return

        const nextState = sendInterestNotification({
            sender: currentMember,
            receiver: profile,
        })

        setSentInterestIds(nextState.sentInterestIds)
        updateFeedback(`Interest sent to ${getFullName(profile)}. The other member will see a notification.`)
    }

    return (
        <motion.div id="member-view" className="space-y-6">
            <div className="panel-card overflow-hidden p-5 sm:p-6">
                <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_380px]">
                    <div>
                        {!portalMode && (
                            <button
                                type="button"
                                className="inline-flex min-h-[42px] items-center gap-2 rounded-full border border-borderColor bg-white px-4 py-2 text-sm font-semibold text-tColor transition hover:border-primeColor hover:text-primeColor"
                                onClick={() => window.history.length > 1 ? history.goBack() : history.push('/user')}
                            >
                                <HiOutlineArrowLeft className="text-base" />
                                Go back
                            </button>
                        )}
                        <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Your profile</p>
                        <h1 className="mt-2 text-3xl font-semibold text-tColor sm:text-4xl">
                            {currentMemberName}
                        </h1>
                        <p className="mt-3 max-w-3xl text-base leading-8 text-tpColor">
                            Search profiles, save the ones you like, and press <span className="font-semibold text-tColor">I am interested</span> when you want us to notify the other member.
                        </p>

                        <div className="mt-6 grid gap-4 sm:grid-cols-3">
                            <div className="rounded-3xl bg-[#fff6f1] p-4">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">Suggested profiles</p>
                                <p className="mt-2 text-3xl font-semibold text-tColor">{rankedMatches.length}</p>
                            </div>
                            <div className="rounded-3xl bg-slate-50 p-4">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">Shortlisted</p>
                                <p className="mt-2 text-3xl font-semibold text-tColor">{shortlistedIds.length}</p>
                            </div>
                            <div className="rounded-3xl bg-slate-50 p-4">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">Interest sent</p>
                                <p className="mt-2 text-3xl font-semibold text-tColor">{sentInterestCount}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-[30px] bg-gradient-to-br from-[#fff8f2] via-[#fffdf9] to-[#eef6fb] p-5 shadow-soft">
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Your plan</p>
                        <h2 className="mt-2 text-2xl font-semibold text-tColor">{planDetails?.planId?.planName || "Standard member"}</h2>
                        <div className="mt-5 grid gap-3">
                            <div className="rounded-2xl bg-white/80 p-4">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">Plan valid till</p>
                                <p className="mt-2 text-lg font-semibold text-tColor">{formatDate(planDetails?.planValidityDate)}</p>
                            </div>
                            <div className="rounded-2xl bg-white/80 p-4">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">Your place</p>
                                <p className="mt-2 text-lg font-semibold text-tColor">{currentMemberLocation || "-"}</p>
                            </div>
                            <div className="rounded-2xl bg-white/80 p-4">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">What you are looking for</p>
                                <p className="mt-2 text-base font-semibold text-tColor">
                                    {resolveOccupation(currentMember?.additionalInformation?.expectedOccupation) !== '-' ? resolveOccupation(currentMember?.additionalInformation?.expectedOccupation) : (currentMember?.additionalInformation?.expectation || "-")}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {feedback ? (
                <div className="rounded-3xl border border-[#f5cfb0] bg-[#fff8ef] px-5 py-4 text-sm font-semibold text-[#8a5520] shadow-soft">
                    {feedback}
                </div>
            ) : null}

            {portalMode && memberNotice ? (
                <div className="rounded-3xl border border-[#d6e9f7] bg-[#f4faff] px-5 py-4 text-sm font-semibold text-[#24517a] shadow-soft">
                    {memberNotice}
                </div>
            ) : null}

            {incomingInterestNotifications.length > 0 ? (
                <div className="panel-card p-5">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">New interest</p>
                    <div className="mt-4 grid gap-3">
                        {incomingInterestNotifications.slice(0, 3).map((entry) => (
                            <div key={entry.id} className="rounded-2xl border border-borderColor bg-slate-50 px-4 py-4 text-sm text-tColor">
                                <p className="font-semibold">{entry.message}</p>
                                <p className="mt-1 text-tpColor">Ref ID: {entry.senderRefId} • {formatDate(entry.createdAt)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {loading ? (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                    <div className="panel-card h-[420px] animate-pulse xl:col-span-5" />
                    <div className="panel-card h-[560px] animate-pulse xl:col-span-7" />
                </div>
            ) : !currentMember ? (
                <div className="panel-card p-8 text-center text-tpColor">
                    Member details are not available right now.
                </div>
            ) : (
                <>
                    <div className="panel-card p-5 sm:p-6">
                        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">Search profiles</p>
                                <h2 className="mt-2 text-2xl font-semibold text-tColor">Suggested matches for you</h2>
                                <p className="mt-2 text-sm leading-7 text-tpColor">
                                    Type name, job, or place to find profiles quickly.
                                </p>
                            </div>
                            <label className="relative block">
                                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-tpColor">
                                    <HiOutlineMagnifyingGlass className="text-xl" />
                                </span>
                                <input
                                    type="text"
                                    className="min-h-[54px] w-full min-w-[280px] rounded-[26px] border border-borderColor bg-white pl-12 pr-4 text-base font-semibold text-tColor shadow-soft outline-none transition focus:border-primeColor"
                                    placeholder="Search by name, job, or place"
                                    value={matchSearch}
                                    onChange={(event) => setMatchSearch(event.target.value)}
                                />
                            </label>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
                        <div className="grid gap-4 xl:col-span-5">
                            {filteredMatches.length > 0 ? filteredMatches.map((profile) => {
                                const isSelected = selectedMatch?._id === profile._id
                                const isShortlisted = shortlistedIds.includes(profile._id)
                                const isInterested = sentInterestIds.includes(profile._id)
                                const location = getMemberLocation(profile)

                                return (
                                    <div
                                        key={profile._id}
                                        className={`rounded-[28px] border px-5 py-5 transition ${
                                            isSelected
                                                ? "border-primeColor bg-primeSoft/60 shadow-soft"
                                                : "border-borderColor bg-white hover:border-primeColor/30 hover:shadow-soft"
                                        }`}
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div>
                                                <p className="text-xl font-semibold text-tColor">{getFullName(profile)}</p>
                                                <p className="mt-2 text-sm leading-7 text-tpColor">
                                                    Age {calculateAge(profile?.dateOfBirth)} • {profile?.occupationDetail || profile?.occupation || "-"}
                                                </p>
                                                <p className="text-sm leading-7 text-tpColor">{location || "-"}</p>
                                            </div>
                                            <span className="rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-primeColor shadow-soft">
                                                Match {profile.__matchScore}
                                            </span>
                                        </div>

                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {(profile.__matchReasons || []).map((reason) => (
                                                <span key={reason} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-tColor shadow-soft">
                                                    {reason}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                            <button
                                                type="button"
                                                className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-2xl border border-borderColor bg-white px-4 text-sm font-bold text-tColor transition hover:border-primeColor hover:text-primeColor"
                                                onClick={() => setSelectedMatchId(profile._id)}
                                            >
                                                View details
                                            </button>
                                            <button
                                                type="button"
                                                className={`inline-flex min-h-[50px] items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold transition ${
                                                    isShortlisted
                                                        ? "bg-slate-700 text-white"
                                                        : "border border-borderColor bg-white text-tColor hover:border-primeColor hover:text-primeColor"
                                                }`}
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    handleShortlistToggle(profile._id)
                                                }}
                                            >
                                                <HiOutlineBookmark className="text-lg" />
                                                {isShortlisted ? "Saved" : "Save profile"}
                                            </button>
                                            <button
                                                type="button"
                                                className={`inline-flex min-h-[50px] items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold transition ${
                                                    isInterested
                                                        ? "bg-[#e7f8ef] text-[#20784f]"
                                                        : "bg-primeColor text-white hover:bg-primeDark"
                                                }`}
                                                onClick={(event) => {
                                                    handleSendInterest(profile)
                                                }}
                                                disabled={isInterested}
                                            >
                                                <HiOutlineHeart className="text-lg" />
                                                {isInterested ? "Interest sent" : "I am interested"}
                                            </button>
                                        </div>
                                    </div>
                                )
                            }) : (
                                <div className="panel-card p-8 text-center text-tpColor">
                                    No profiles found for this search.
                                </div>
                            )}
                        </div>

                        <div className="space-y-6 xl:col-span-7">
                            {selectedMatch ? (() => {
                                const isPaid = !!(planDetails && planDetails.planValidityDate && new Date(planDetails.planValidityDate) > new Date())
                                const masked = <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-bold tracking-widest text-slate-400 select-none">● ● ●</span>
                                const fi = selectedMatch?.familyInformation || {}
                                const ci = selectedMatch?.contactInformation || {}
                                const ai = selectedMatch?.additionalInformation || {}
                                return (
                                <>
                                    {/* ── Hero card ── */}
                                    <div className="panel-card overflow-hidden">
                                        <div className="grid grid-cols-1 gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
                                            <div className="bg-gradient-to-br from-[#fff5f2] via-[#fffdfa] to-[#eef6fb] p-5">
                                                <PhotoProvider>
                                                    <PhotoView src={selectedMainImage}>
                                                        <img src={selectedMainImage} alt={selectedMatchName} className="h-[320px] w-full rounded-[28px] object-cover shadow-soft cursor-pointer" />
                                                    </PhotoView>
                                                </PhotoProvider>
                                                {selectedMatchGallery.length > 1 && (
                                                    <PhotoProvider>
                                                        <div className="mt-3 flex gap-2 overflow-x-auto">
                                                            {selectedMatchGallery.slice(1).map((img, idx) => (
                                                                <PhotoView key={idx} src={img.imageUrl}>
                                                                    <img src={img.imageUrl} alt="" className="h-16 w-16 shrink-0 cursor-pointer rounded-xl object-cover shadow-soft transition hover:scale-105" />
                                                                </PhotoView>
                                                            ))}
                                                        </div>
                                                    </PhotoProvider>
                                                )}
                                            </div>
                                            <div className="p-6">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="rounded-full bg-primeSoft px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-primeColor">
                                                        {selectedMatch?.profileType || "Member"}
                                                    </span>
                                                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-slate-600">
                                                        Ref ID {selectedMatch?.refId || "-"}
                                                    </span>
                                                    {!isPaid && (
                                                        <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 border border-amber-200">
                                                            Free plan — upgrade to unlock contact details
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="mt-4 text-2xl font-semibold text-tColor">{selectedMatchName}</h3>
                                                <p className="mt-1 text-sm font-semibold text-primeColor">{selectedMatch?.refId}</p>
                                                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-tpColor">
                                                    <span>{calculateAge(selectedMatch?.dateOfBirth)} yrs</span>
                                                    {selectedMatch?.heightInFeet && <span>• {selectedMatch.heightInFeet}</span>}
                                                    {(selectedMatch?.occupationDetail || selectedMatch?.occupation) && <span>• {selectedMatch?.occupationDetail || selectedMatch?.occupation}</span>}
                                                    {selectedMatchLocation && <span>• {selectedMatchLocation}</span>}
                                                </div>
                                                {ai.expectation && (
                                                    <p className="mt-3 text-sm leading-7 text-tpColor italic">"{ai.expectation}"</p>
                                                )}
                                                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                                    <button
                                                        type="button"
                                                        className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold transition ${
                                                            shortlistedIds.includes(selectedMatch._id)
                                                                ? "bg-slate-700 text-white"
                                                                : "border border-borderColor bg-white text-tColor hover:border-primeColor hover:text-primeColor"
                                                        }`}
                                                        onClick={() => handleShortlistToggle(selectedMatch._id)}
                                                    >
                                                        <HiOutlineBookmark className="text-lg" />
                                                        {shortlistedIds.includes(selectedMatch._id) ? "Saved" : "Save profile"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl px-5 text-sm font-bold transition ${
                                                            sentInterestIds.includes(selectedMatch._id)
                                                                ? "bg-[#e7f8ef] text-[#20784f]"
                                                                : "bg-primeColor text-white hover:bg-primeDark"
                                                        }`}
                                                        onClick={() => handleSendInterest(selectedMatch)}
                                                        disabled={sentInterestIds.includes(selectedMatch._id)}
                                                    >
                                                        <HiOutlineHeart className="text-lg" />
                                                        {sentInterestIds.includes(selectedMatch._id) ? "Interest sent" : "I am interested"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ── Detail sections: same structure as admin PreviewUser ── */}
                                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">

                                        {/* Family Information */}
                                        <div className="preview-user-card flex flex-col gap-4 p-5">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eef6fb] text-accent">
                                                    <HiOutlineHomeModern className="text-lg" />
                                                </span>
                                                <p className="text-lg font-bold text-primeColor">Family Information</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Father name</p>
                                                <p className="font-bold text-primeColor text-right">
                                                    {[fi.fatherFirstName, fi.fatherSurname].filter(Boolean).join(" ") || "--"}
                                                </p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Father Occupation</p>
                                                <p className="font-bold text-primeColor text-right">{fi.fatherOccupation || "-"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Mother name</p>
                                                <p className="font-bold text-primeColor text-right">
                                                    {[fi.motherFirstName, fi.motherSurname].filter(Boolean).join(" ") || "--"}
                                                </p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Mother Occupation</p>
                                                <p className="font-bold text-primeColor text-right">{fi.motherOccupation || "-"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Male Siblings</p>
                                                <p className="font-bold text-primeColor">{fi.numberOfMaleSiblings ?? fi.numberOfMaleSibilings ?? "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Female Siblings</p>
                                                <p className="font-bold text-primeColor">{fi.numberOfFemaleSiblings ?? fi.numberOfFemaleSibilings ?? "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Siblings Details</p>
                                                <p className="font-bold text-primeColor text-right">{fi.siblingsDetails || "--"}</p>
                                            </div>
                                            {fi.assetInfo && (
                                                <div className="preview-user-detail-row">
                                                    <p>Asset Info</p>
                                                    <p className="font-bold text-primeColor text-right">{fi.assetInfo}</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Contact Information — gated */}
                                        <div className="preview-user-card flex flex-col gap-4 p-5 relative">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primeSoft text-primeColor">
                                                    <HiOutlinePhone className="text-lg" />
                                                </span>
                                                <p className="text-lg font-bold text-primeColor">Contact Information</p>
                                            </div>
                                            {!isPaid && (
                                                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-[inherit] bg-white/80 backdrop-blur-[2px]">
                                                    <HiOutlineLockClosed className="text-3xl text-amber-500" />
                                                    <p className="text-sm font-bold text-tColor text-center px-4">Upgrade to a paid plan to view contact details</p>
                                                </div>
                                            )}
                                            <div className="preview-user-detail-row">
                                                <p>Phone Number</p>
                                                <p className="font-bold text-primeColor">{isPaid ? (ci.mobileNumber || "-") : masked}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>WhatsApp</p>
                                                <p className="font-bold text-primeColor">{isPaid ? (ci.whatsappNumber || "-") : masked}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Email</p>
                                                <p className="font-bold text-primeColor text-right">{isPaid ? (ci.email || "-") : masked}</p>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <p className="text-sm text-tpColor">Present Address</p>
                                                {isPaid ? (
                                                    <p className="font-bold text-primeColor text-sm leading-6">
                                                        {[
                                                            ci.presentAddress?.addressLine1,
                                                            ci.presentAddress?.addressLine2,
                                                            ci.presentAddress?.city,
                                                            ci.presentAddress?.district,
                                                            ci.presentAddress?.state,
                                                            ci.presentAddress?.pinCode,
                                                        ].filter(Boolean).join(", ") || "-"}
                                                    </p>
                                                ) : masked}
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <p className="text-sm text-tpColor">Native Address</p>
                                                {isPaid ? (
                                                    <p className="font-bold text-primeColor text-sm leading-6">
                                                        {[
                                                            ci.nativeAddress?.addressLine1,
                                                            ci.nativeAddress?.addressLine2,
                                                            ci.nativeAddress?.city,
                                                            ci.nativeAddress?.district,
                                                            ci.nativeAddress?.state,
                                                            ci.nativeAddress?.pinCode,
                                                        ].filter(Boolean).join(", ") || "-"}
                                                    </p>
                                                ) : masked}
                                            </div>
                                        </div>

                                        {/* Expected Information */}
                                        <div className="preview-user-card flex flex-col gap-4 p-5">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff8ea] text-[#d69a00]">
                                                    <HiOutlineHeart className="text-lg" />
                                                </span>
                                                <p className="text-lg font-bold text-primeColor">Expected Information</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Expected Income</p>
                                                <p className="font-bold text-primeColor text-right">{ai.expectedSalary || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Expected Status</p>
                                                <p className="font-bold text-primeColor text-right">{renderList(ai.expectedStatus)}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Zodiac Signs</p>
                                                <p className="font-bold text-primeColor text-right">{renderList(ai.expectedZodiacSigns)}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Stars</p>
                                                <p className="font-bold text-primeColor text-right">{renderList(ai.expectedStars)}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Expected Profession</p>
                                                <p className="font-bold text-primeColor text-right">{resolveOccupation(ai.expectedOccupation)}</p>
                                            </div>
                                            {ai.expectation && (
                                                <div className="rounded-xl bg-primeSoft/40 px-4 py-3">
                                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor mb-1">Expectation</p>
                                                    <p className="text-sm font-semibold text-tColor">{ai.expectation}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── Birth Information + Profile basics ── */}
                                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                                        <div className="preview-user-card flex flex-col gap-4 p-5">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primeSoft text-primeColor">
                                                    <HiOutlineIdentification className="text-lg" />
                                                </span>
                                                <p className="text-lg font-bold text-primeColor">Basic Details</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Date of Birth</p>
                                                <p className="font-bold text-primeColor">{formatDate(selectedMatch?.dateOfBirth)}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Age</p>
                                                <p className="font-bold text-primeColor">{calculateAge(selectedMatch?.dateOfBirth)} yrs</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Marital Status</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.maritalStatus || "-"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Height</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.heightInFeet || "-"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Income / Month</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.salary ? `${selectedMatch.salary} ${selectedMatch?.salaryMode || ""}` : "-"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Qualification</p>
                                                <p className="font-bold text-primeColor text-right">{selectedMatch?.otherEducationalQualification || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Work Location</p>
                                                <p className="font-bold text-primeColor text-right">{selectedMatch?.occupationLocation || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Zodiac Sign</p>
                                                <p className="font-bold text-primeColor">{ai.zodiacSign || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Star</p>
                                                <p className="font-bold text-primeColor">{ai.star ? `${ai.star}${ai.padham ? ` – ${ai.padham}` : ""}` : "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Flaws</p>
                                                <p className="font-bold text-primeColor text-right">{renderList(ai.flaws)}</p>
                                            </div>
                                        </div>

                                        <div className="preview-user-card flex flex-col gap-4 p-5">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eef6fb] text-accent">
                                                    <HiOutlineClock className="text-lg" />
                                                </span>
                                                <p className="text-lg font-bold text-primeColor">Birth Information</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Birth Time</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.birthTime || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Birth Place</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.birthPlace || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Dhisai Iruppu</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.thisaiIruppu || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Thisai Iruppu Date</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.thisaiIruppuDate || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Thisai Iruppu Month</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.thisaiIruppuMonth || "--"}</p>
                                            </div>
                                            <div className="preview-user-detail-row">
                                                <p>Thisai Iruppu Year</p>
                                                <p className="font-bold text-primeColor">{selectedMatch?.thisaiIruppuYear || "--"}</p>
                                            </div>
                                            <div className="mt-2 border-t border-borderColor pt-4">
                                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor mb-3">Photos</p>
                                                <PhotoProvider>
                                                    <div className="grid grid-cols-3 gap-2">
                                                        {selectedMatchGallery.length > 0 ? selectedMatchGallery.map((image, index) => (
                                                            <PhotoView key={`${image.imageUrl}-${index}`} src={image.imageUrl}>
                                                                <img src={image.imageUrl} alt="" className="h-20 w-full cursor-pointer rounded-xl object-cover shadow-soft transition hover:scale-[1.02]" />
                                                            </PhotoView>
                                                        )) : (
                                                            <div className="col-span-3 rounded-xl border border-dashed border-borderColor bg-slate-50 p-4 text-xs text-tpColor">
                                                                No photos yet.
                                                            </div>
                                                        )}
                                                    </div>
                                                </PhotoProvider>
                                            </div>
                                        </div>
                                    </div>
                                </>
                                )
                            })() : null}
                        </div>
                    </div>

                    {shortlistedMatches.length > 0 ? (
                        <div className="panel-card p-5 sm:p-6">
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">Saved by you</p>
                            <h2 className="mt-2 text-2xl font-semibold text-tColor">Your shortlist</h2>
                            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {shortlistedMatches.map((profile) => (
                                    <button
                                        key={profile._id}
                                        type="button"
                                        className="rounded-3xl border border-borderColor bg-slate-50 px-4 py-4 text-left transition hover:border-primeColor/30 hover:bg-white"
                                        onClick={() => setSelectedMatchId(profile._id)}
                                    >
                                        <p className="text-lg font-semibold text-tColor">{getFullName(profile)}</p>
                                        <p className="mt-2 text-sm text-tpColor">
                                            {profile?.occupationDetail || profile?.occupation || "-"} • {getMemberLocation(profile) || "-"}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </>
            )}
        </motion.div>
    )
}

export default MemberView
